import { inject } from 'vue';
import { useStore } from 'vuex';

/**
 * Works with form input fields to leverage our schematized field definitions.
 * (see LabeledSelect.vue for example of use)
 *
 * Append emits to your component emit definition
 * Append props to your component prop definition
 *
 * @param {*} fieldRef
 * @param {*} hardLockRef
 * @param {*} mandatoryRef
 * @param {*} messageRef
 * @param {*} schemaIdRef
 * @returns {
 *  enabled: boolean
 *  label: ref
 *  ariaLabel: string
 *  locked: ref
 *  minimum: any
 *  mask: any
 *  propertyBySchema: ref
 *  getLabelByProperty: function
 * }
 */
export default function useSchematizedField(
  fieldRef,
  hardLockRef,
  mandatoryRef,
  messageRef,
  schemaIdRef,
) {
  const $bct = inject('translator');
  const store = useStore();

  const schema = computed(() => {
    return store.state.schemas[schemaIdRef.value];
  });

  const schemaProperties = computed(() => {
    if (!fieldRef.value) return null;
    if (!schemaIdRef.value) throw new Error('schemaId required to use field');
    if (!schema.value) {
      throw new Error(`Schema missing from site schemas - ID: ${schemaIdRef.value}`);
    }

    let namespace;
    if (Object.keys(customProperties.value).includes(fieldRef.value)) {
      namespace = fieldRef.value
        .split('.')
        .reduce((value, index) => value[index], customProperties.value || {});
    } else {
      namespace = fieldRef.value
        .split('.')
        .reduce((value, index) => value[index], properties.value);
    }
    return namespace || {};
  });

  const enabled = computed(() => {
    if (!fieldRef.value) return true;
    return schemaProperties.value?.enabled;
  });

  const label = computed(() => {
    if (messageRef.value) return $bct(messageRef.value);
    return $bct(schemaProperties.value?.alias || schemaProperties.value?.title || '');
  });

  const ariaLabel = computed(() => {
    if (!label.value) return null;

    const translatedLabel = $bct(label.value);
    if (mandatoryRef.value) {
      const translatedRequired = $bct('required');
      return `${translatedLabel} - ${translatedRequired}`;
    }

    return translatedLabel;
  });

  const locked = computed(() => {
    if (hardLockRef.value) return true;
    if (schemaProperties.value) {
      return !(schemaProperties.value.editRoles || []).includes(store.state.role);
    }
    return false;
  });

  const minimum = computed(() => {
    return schemaProperties.value?.minimum;
  });

  const schemaMask = computed(() => {
    return schemaProperties.value?.mask;
  });

  const properties = computed(() => {
    return schema.value?.definition?.properties;
  });

  const customProperties = computed(() => {
    return properties.value?.custom?.properties || {};
  });

  const propertyBySchema = computed(() => {
    if (fieldRef.value) {
      return (
        fieldRef.value.split('.').reduce((value, index) => value[index], properties.value) || {}
      );
    }
    return null;
  });

  function getLabelByProperty(property, defaultLabel) {
    return properties.value?.[property]?.alias || defaultLabel;
  }

  return {
    enabled,
    label,
    ariaLabel,
    locked,
    minimum,
    schemaMask,
    propertyBySchema,
    getLabelByProperty,
  };
}

useSchematizedField.props = {
  field: {
    type: String,
    default: null,
  },
  hardLock: {
    type: Boolean,
    default: false,
  },
  hardLockMessage: {
    type: String,
    default: 'Locked',
  },
  mandatory: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: null,
  },
  schemaId: {
    type: String,
    default: null,
  },
  enforceMinMaxRules: {
    type: Boolean,
    default: true,
  },
};

// Must match order of params accepted by composable
useSchematizedField.paramKeys = ['field', 'hardLock', 'mandatory', 'message', 'schemaId'];
